import React, { useCallback, useEffect, useState } from "react";

import Col from "antd/es/col";
import Radio from "antd/es/radio";
import { RadioChangeEvent } from "antd/es/radio/interface";
import Row from "antd/es/row";
import Select from "antd/es/select";
import Space from "antd/es/space";
import cn from "classnames";
import { useIntl } from "react-intl";

import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import { Labeled } from "@mapmycustomers/ui";

import EntityBadge from "@app/component/EntityBadge";

import styles from "./RevenueExtraFilter.module.scss";

export const NOT_SELECTED_VALUE = 0;

interface Props {
  funnels: Funnel[];
  onFunnelSelect: (funnel: Funnel["id"]) => void;
  onStageSelect: (stages: Stage["id"]) => void;
  selectedFunnel: Funnel["id"];
  selectedStage: Stage["id"];
  stages: Stage[];
}

export const RevenueExtraFilter: React.FC<Props> = ({
  funnels,
  onFunnelSelect,
  onStageSelect,
  selectedFunnel,
  selectedStage,
  stages,
}) => {
  const intl = useIntl();

  const [useExtraFilter, setUseExtraFilter] = useState<boolean>(false);

  const onExtraFilterChange = useCallback(
    ({ target: { value } }: RadioChangeEvent) => {
      if (!value) {
        onFunnelSelect(NOT_SELECTED_VALUE);
        onStageSelect(NOT_SELECTED_VALUE);
      }
      setUseExtraFilter(value);
    },
    [onFunnelSelect, onStageSelect, setUseExtraFilter]
  );

  useEffect(() => {
    onStageSelect(NOT_SELECTED_VALUE);
  }, [onStageSelect, selectedFunnel]);

  return (
    <div className={styles.container}>
      <Radio.Group onChange={onExtraFilterChange} value={useExtraFilter}>
        <Space direction="vertical">
          <Radio value={false}>
            {intl.formatMessage({
              id: "reports.overview.extraHeatMapFilter.filterRadio.hideFilter",
              defaultMessage: "All open funnels",
              description: "Hide option of radio group of Heat map section extra filter",
            })}
          </Radio>
          <Radio value={true}>
            {intl.formatMessage({
              id: "reports.overview.extraHeatMapFilter.filterRadio.showFilter",
              defaultMessage: "Select funnel & stage",
              description: "Show option of radio group of Heat map section extra filter",
            })}
          </Radio>
        </Space>
      </Radio.Group>
      {useExtraFilter && (
        <Space direction="vertical" size="middle">
          <Labeled
            className={styles.fullWidth}
            label={intl.formatMessage({
              id: "reports.overview.extraHeatMapFilter.funnelSelect.label",
              defaultMessage: "Sales Funnel",
              description: "Label of Funnel select in Heat map section extra filter",
            })}
            labelClassName={styles.selectLabel}
          >
            <Col span={24}>
              <Row>
                <Select<Stage["id"]>
                  className={cn(styles.select, styles.fullWidth)}
                  onChange={onFunnelSelect}
                  value={selectedFunnel}
                >
                  <Select.Option value={NOT_SELECTED_VALUE}>
                    {intl.formatMessage({
                      id: "reports.overview.extraHeatMapFilter.funnelSelect.defaultOption",
                      defaultMessage: "Select Funnel",
                      description:
                        "Default option of Funnel select in Heat map section extra filter",
                    })}
                  </Select.Option>
                  {funnels.map((funnel) => (
                    <Select.Option key={funnel.id} value={funnel.id}>
                      {funnel.name}
                    </Select.Option>
                  ))}
                </Select>
              </Row>
            </Col>
          </Labeled>

          <Labeled
            className={styles.fullWidth}
            label={intl.formatMessage({
              id: "reports.overview.extraHeatMapFilter.stageSelect.label",
              defaultMessage: "Stage",
              description: "Label of Stage select in Heat map section extra filter",
            })}
            labelClassName={styles.selectLabel}
          >
            <Col span={24}>
              <Row>
                <Select<Stage["id"]>
                  className={cn(styles.select, styles.fullWidth)}
                  disabled={!selectedFunnel || !stages.length}
                  onChange={onStageSelect}
                  value={selectedStage}
                >
                  <Select.Option value={NOT_SELECTED_VALUE}>
                    {intl.formatMessage({
                      id: "reports.overview.extraHeatMapFilter.stageSelect.defaultOption",
                      defaultMessage: "All stages",
                      description:
                        "Default option of Stage select in Heat map section extra filter",
                    })}
                  </Select.Option>
                  {stages.map((stage) => (
                    <Select.Option key={stage.id} value={stage.id}>
                      <EntityBadge entity={stage} key={stage.id} />
                    </Select.Option>
                  ))}
                </Select>
              </Row>
            </Col>
          </Labeled>
        </Space>
      )}
    </div>
  );
};
